import { useIsFormDisabled } from "@/flokit/compound/inputs/contexts/disabled-form-context";
import { WithUnsafeClassName } from "@/flokit/types/with-unsafe-classname";
import { DisabledWithTooltip } from "@/flokit/utils/disabled-with-tooltip/disabled-with-tooltip";
import { DisabledWithTooltipWrapper } from "@/flokit/utils/disabled-with-tooltip/disabled-with-tooltip-wrapper";
import { normalizeDisabledWithTooltip } from "@/flokit/utils/disabled-with-tooltip/normalize-disabled-with-tooltip";
import { cn } from "@/flokit/utils/styling/cn";
import { VariantProps } from "class-variance-authority";
import React, { HTMLProps } from "react";
import { FiLoader } from "react-icons/fi";
import { buttonStyles } from "./styles";

export type FloKitButtonProps = VariantProps<typeof buttonStyles> &
  Omit<
    HTMLProps<HTMLButtonElement>,
    "className" | "type" | "size" | "color" | "width" | "disabled"
  > &
  WithUnsafeClassName<{
    children: React.ReactNode;
    type?: "button" | "submit" | "reset";
    loading?: boolean;
    disabled?: DisabledWithTooltip;
    /**
     * Product wants the following:
     *
     * - Clicking anywhere on card, except on nested buttons, links to a page
     * - When a nested button is disabled, clicking on that nested button should do nothing, ie not link to the page
     *
     * `pointer-events: none` makes this impossible since it prevents the `<button>` from absorbing any clicks when disabled, leading to the link being clicked. `event#stopPropagation` does not work. So we add this prop to achieve this behavior.
     *
     * TODO: The `pointer-events: none` was [originally brought in](https://github.com/Flo-Recruit/FloRecruitSpringBoot/pull/2466) to make the button work properly with RadixUI's `<Tooltip>`. I think we could remove this prop if we make the `pointer-events: none` more targeted, only applying if there's _actually_ a tooltip (i.e. a message is provided).
     */
    UNSAFE_overridePointerEventsNoneWhenDisabled?: boolean;
  }>;

export const Button = React.forwardRef(
  (
    {
      type,
      children,
      color,
      size,
      loading,
      disabled,
      width,
      wrapText,
      // eslint-disable-next-line camelcase
      UNSAFE_className,
      // eslint-disable-next-line camelcase
      UNSAFE_overridePointerEventsNoneWhenDisabled,
      ...props
    }: FloKitButtonProps,
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const disabledWithTooltip = normalizeDisabledWithTooltip(disabled);
    const hasDisabledProps = disabled !== undefined;
    const isWithinDisabledForm = useIsFormDisabled();
    // if the user has explicity set the disabled prop, use that
    // otherwise default to the disabled form context
    const isDisabled = hasDisabledProps
      ? disabledWithTooltip.isDisabled
      : isWithinDisabledForm;

    return (
      <DisabledWithTooltipWrapper {...disabledWithTooltip}>
        <button
          {...props}
          disabled={isDisabled || loading}
          className={cn(
            buttonStyles({
              color,
              size,
              width,
              wrapText,
            }),
            UNSAFE_className,
          )}
          type={type}
          ref={ref}
          style={{
            pointerEvents:
              // eslint-disable-next-line camelcase
              isDisabled && !UNSAFE_overridePointerEventsNoneWhenDisabled
                ? "none"
                : undefined,
          }}
        >
          {loading ? (
            <>
              <span
                className={cn(
                  // Pass layout/position properties through this <span> to the children
                  "contents",
                  // Hide children while preserving layout
                  "invisible",
                )}
                aria-hidden={true}
              >
                {children}
              </span>
              <FiLoader
                className={cn(
                  // Center spinner on top of content, scale with size of button
                  "margin-auto absolute h-full",
                  "animate-spin",
                )}
                aria-label="Loading"
              />
            </>
          ) : (
            children
          )}
        </button>
      </DisabledWithTooltipWrapper>
    );
  },
);
